import { useId, useMemo } from "react";

import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useTranslations } from "@/shared/lang/useTranslations";

import { ACCESS_FILTER_ALL, type AccessScopeFilter } from "../constants/access.constants";

export interface AccessCatalogToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  /** What the search box says it searches. The two catalogs match on different fields. */
  searchPlaceholder: string;
  scope: AccessScopeFilter;
  onScopeChange: (value: AccessScopeFilter) => void;
}

/**
 * Search plus the tenancy filter, shared by the roles and policies catalogs — the two differ only in what the
 * search matches, which is a prop.
 *
 * **No bucket control here**: the tile strip above is that filter, so repeating it would state the same axis
 * twice.
 *
 * Both filters run in the browser and there is no minimum term, because the whole catalog is already loaded —
 * the result is instant and describes every row that exists. That is the opposite trade-off from the users
 * roster, where search is a server round trip and a two-character term would scan the table. The difference
 * is the endpoint, not a change of mind.
 */
export function AccessCatalogToolbar({
  query,
  onQueryChange,
  searchPlaceholder,
  scope,
  onScopeChange,
}: AccessCatalogToolbarProps) {
  const { t } = useTranslations("settings");
  const scopeFieldId = useId();

  const scopeOptions = useMemo(
    () => [
      { value: ACCESS_FILTER_ALL, label: t("access_page_scope_all") },
      // The two tenancy values reuse the badge's own labels, so the filter and the column cannot disagree.
      { value: "global", label: t("access_scope_global") },
      { value: "org", label: t("access_scope_org") },
    ],
    [t],
  );

  return (
    <ColocaSurface padding="md">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="w-full lg:max-w-sm">
          <ColocaSearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </div>

        <div className="flex w-full items-center gap-2 lg:w-auto">
          <label htmlFor={scopeFieldId} className="shrink-0 text-xs text-muted-foreground">
            {t("access_page_scope_label")}
          </label>
          <ColocaSelect
            id={scopeFieldId}
            value={scope}
            onValueChange={(next) => onScopeChange((next ?? ACCESS_FILTER_ALL) as AccessScopeFilter)}
            options={scopeOptions}
            className="w-full sm:w-48"
          />
        </div>
      </div>
    </ColocaSurface>
  );
}
